import { QuestionIcon } from '@phosphor-icons/react'
import { mappedColors, typography } from '@pixee/design-system'
import { useQueryClient } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import invariant from 'tiny-invariant'
import { match, P } from 'ts-pattern'
import { useNewerScanBanner } from '../hooks/use-newer-scan-banner.ts'
import { invalidateGetScans } from '../utils/api-client/api-hooks.ts'
import type { ScanResponse, Tool } from '../utils/api-client/api-schemas.ts'
import { ScanStatusBadge } from './badges/scan-status-badge.tsx'
import { DefaultButton } from './buttons/default-button.tsx'
import * as styles from './findings-tab-header.css.ts'
import { ResolutionPanelScanInfo } from './resolution-panel/resolution-panel-scan-info.tsx'
import { ResolutionPanel } from './resolution-panel/resolution-panel.tsx'
import {
  CompositionBarsContent,
  QuickViewAnalysisMetrics,
  shouldShowResolutionPanel,
  useScanDetailsHeaderBase,
  type ScanDetailsHeaderPureProps,
} from './scan-details-header-shared.tsx'
import { Spinner } from './spinner.tsx'
import { TooltipWithHover } from './tooltip.tsx'

// #region Wrapper

export type FindingsTabHeaderProps = {
  scanId: string
  repositoryId?: string
  tool?: Tool
  /**
   * Only scans on this branch count as newer; `undefined` for every branch. Required, so a caller
   * opts out of the branch scope explicitly rather than by leaving it off.
   */
  branch: string | undefined
}

export const FindingsTabHeader: React.FC<FindingsTabHeaderProps> = ({ scanId, repositoryId, tool, branch }) => {
  const props = useScanDetailsHeaderBase({ scanId })
  const queryClient = useQueryClient()
  const newerScan = useNewerScanBanner({ currentScanId: scanId, repositoryId, tool, branch })
  const isStale = newerScan?.state === 'has-analysis'
  const onRefresh = () => invalidateGetScans(queryClient)

  return (
    <FindingsTabHeaderPure
      {...props}
      isStale={isStale}
      onRefresh={onRefresh}
      resolutionPanelSlot={slots => <ResolutionPanel scanId={scanId} {...slots} />}
    />
  )
}

// #endregion

// #region Pure Component

export type FindingsTabHeaderPureProps = ScanDetailsHeaderPureProps & {
  isStale?: boolean
  onRefresh?: () => unknown
  /**
   * Builds the Resolution panel from the header slots this component supplies. A render prop rather
   * than a node because the panel reads the findings filter store, so only the impure wrapper can
   * construct it — while the content it shows stays owned by this pure component.
   */
  resolutionPanelSlot?: (slots: { header: React.ReactNode; actions: React.ReactNode }) => React.ReactNode
}

export const FindingsTabHeaderPure: React.FC<FindingsTabHeaderPureProps> = ({
  scan,
  isScanLoading,
  isScanError,
  scanDetailsHeaderState,
  isScanDetailsHeaderStateLoading,
  isScanDetailsHeaderStateError,
  quickViewCompositionBarsProps,
  isStale,
  onRefresh,
  onSkipToInProgressAnalysis,
  resolutionPanelSlot,
}) => {
  if (isScanLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80px' }}>
        <Spinner size="16" label="Loading scan details..." />
      </div>
    )
  }

  if (isScanError || !scan) {
    return (
      <span className={typography.small} style={{ color: mappedColors.neutral.neutral30 }}>
        Error loading scan details.
      </span>
    )
  }

  // The panel carries its own header and controls, so it replaces the whole header block rather
  // than just the composition bars — but the content of those slots stays here. When the panel
  // applies is explained on `shouldShowResolutionPanel` in scan-details-header-shared.tsx.
  if (resolutionPanelSlot && shouldShowResolutionPanel({ scan })) {
    return resolutionPanelSlot({
      header: <ResolutionPanelScanInfo scan={scan} />,
      // No coverage metrics here: the design's panel head carries the data scope and categorization
      // controls, and the funnel tells the same story in more detail than two percentages could.
      actions: (
        <FindingsTabHeaderBadge
          scanDetailsHeaderState={scanDetailsHeaderState}
          isStale={isStale}
          onRefresh={onRefresh}
          hasAnalysisProgress={false}
        />
      ),
    })
  }

  return (
    <div className={styles.container}>
      <FindingsTabHeaderInfo scan={scan} />
      <FindingsTabHeaderBadge scanDetailsHeaderState={scanDetailsHeaderState} isStale={isStale} onRefresh={onRefresh} />
      <div className={styles.compositionBarsContainer}>
        <CompositionBarsContent
          scanDetailsHeaderState={scanDetailsHeaderState}
          isScanDetailsHeaderStateLoading={isScanDetailsHeaderStateLoading}
          isScanDetailsHeaderStateError={isScanDetailsHeaderStateError}
          quickViewCompositionBarsProps={quickViewCompositionBarsProps}
          onSkipToInProgressAnalysis={onSkipToInProgressAnalysis}
        />
      </div>
      <FindingsTabHeaderMetrics
        scanDetailsHeaderState={scanDetailsHeaderState}
        tool={scan.detector}
        quickViewCompositionBarsProps={quickViewCompositionBarsProps}
      />
    </div>
  )
}

// #endregion

// #region Sub-components

type FindingsTabHeaderInfoProps = {
  scan: ScanResponse
}

const FindingsTabHeaderInfo: React.FC<FindingsTabHeaderInfoProps> = ({ scan }) => {
  invariant(scan.imported_at, 'Scan is missing imported_at')
  const scanDateTime = DateTime.fromISO(scan.imported_at)

  return (
    <div className={styles.headerInfoContainer}>
      <div className={styles.dateTimeRow}>
        <p className={styles.dateText}>{scanDateTime.toFormat('M/d/yy')}</p>
        <p className={styles.timeText}>{scanDateTime.toFormat('h:mm:ss a ZZZZ')}</p>
      </div>
      <div className={styles.descriptionRow}>
        <p className={styles.latestScanText}>Latest scan with analysis</p>
        <TooltipWithHover
          trigger={
            <span className={styles.infoIconTrigger}>
              <span className={styles.representativeText}>Representative analysis per finding</span>
              <QuestionIcon className={styles.infoIcon} size={16} weight="regular" />
            </span>
          }
          side="bottom"
          maxWidth={360}
        >
          <p className={styles.tooltipText}>
            For each finding with multiple analyses, Pixee will default to showing results from the most successful
            analysis as a top-level <strong>representative</strong>. If two analyses are equally successful, then the
            most recent of the two will be displayed.
          </p>
        </TooltipWithHover>
      </div>
    </div>
  )
}

type FindingsTabHeaderBadgeProps = {
  scanDetailsHeaderState: ScanDetailsHeaderPureProps['scanDetailsHeaderState']
  isStale?: boolean
  onRefresh?: () => unknown
  /**
   * Whether to show the "N updating" spinner. The Resolution panel reports analysis progress in its
   * own meta row, beside the resolved count, so it suppresses this one rather than stating the same
   * thing twice in two places.
   */
  hasAnalysisProgress?: boolean
}

const FindingsTabHeaderBadge: React.FC<FindingsTabHeaderBadgeProps> = ({
  scanDetailsHeaderState,
  isStale,
  onRefresh,
  hasAnalysisProgress = true,
}) => {
  const staleContent = isStale ? (
    <>
      <ScanStatusBadge variant="stale" />
      <DefaultButton buttonType="primary" size="small" onClick={onRefresh}>
        Refresh page
      </DefaultButton>
    </>
  ) : null

  const stateContent = !hasAnalysisProgress
    ? null
    : match(scanDetailsHeaderState)
        .with({ type: 'first-analysis-batch-in-progress' }, () => <ScanStatusBadge variant="in-progress" />)
        .with({ type: 'findings-with-analysis-complete-and-in-progress' }, ({ statistics }) =>
          statistics.in_progress_analysis > 0 ? (
            <>
              <Spinner size="24" variant="updating" label="Updating findings" />
              <span className={styles.updatingText}>{statistics.in_progress_analysis} updating</span>
            </>
          ) : null
        )
        .otherwise(() => null)

  if (!stateContent && !staleContent) return null

  return (
    <div className={styles.badgeContainer}>
      {stateContent}
      {staleContent}
    </div>
  )
}

type FindingsTabHeaderMetricsProps = {
  scanDetailsHeaderState: ScanDetailsHeaderPureProps['scanDetailsHeaderState']
  tool: ScanResponse['detector']
  quickViewCompositionBarsProps: ScanDetailsHeaderPureProps['quickViewCompositionBarsProps']
}

const FindingsTabHeaderMetrics: React.FC<FindingsTabHeaderMetricsProps> = ({
  scanDetailsHeaderState,
  tool,
  quickViewCompositionBarsProps,
}) =>
  match(scanDetailsHeaderState)
    .with(
      { type: P.union('findings-with-analysis-complete', 'findings-with-analysis-complete-and-in-progress') },
      ({ statistics: globalStats }) => (
        <div className={styles.metricsContainer}>
          <QuickViewAnalysisMetrics
            tool={tool}
            quickViewId={quickViewCompositionBarsProps.quickViewId}
            filteredStatistics={quickViewCompositionBarsProps.filteredStatistics}
            globalStats={globalStats}
            isFilteredDataTransitioning={quickViewCompositionBarsProps.isFilteredDataTransitioning}
          />
        </div>
      )
    )
    .otherwise(() => null)

// #endregion
